import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "text-left flex min-w-0 w-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm p-6 md:p-8 space-y-6" }
const _hoisted_2 = { class: "flex flex-col sm:flex-row sm:items-center justify-between gap-4" }
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-1 md:grid-cols-2 gap-4"
}
const _hoisted_4 = {
  key: 1,
  class: "border border-dashed border-slate-200 bg-slate-50/30 p-10 rounded-2xl text-center space-y-3"
}
const _hoisted_5 = { class: "size-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto" }
const _hoisted_6 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5"
}
const _hoisted_7 = { class: "flex items-start gap-3.5 min-w-0" }
const _hoisted_8 = { class: "min-w-0 flex-1 space-y-1" }
const _hoisted_9 = { class: "flex items-baseline justify-between gap-2" }
const _hoisted_10 = { class: "font-extrabold text-slate-900 truncate text-sm md:text-base leading-snug" }
const _hoisted_11 = { class: "text-xs font-semibold text-slate-400 shrink-0" }
const _hoisted_12 = {
  key: 0,
  class: "text-xs font-bold text-[#1f7aff] truncate"
}
const _hoisted_13 = { class: "text-xs text-slate-500 font-medium line-clamp-2 leading-relaxed" }
const _hoisted_14 = { class: "flex items-center justify-end border-t border-slate-100 pt-2.5" }

import { computed, inject, ref } from "vue";
import { useRouter } from "vue-router";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS, getActivityIconName } from "@/config/dashboardIcons";


export default /*@__PURE__*/_defineComponent({
  __name: 'LatestUpdatesPanel',
  props: {
    data: {},
    loading: { type: Boolean }
  },
  emits: ["view-activity"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const router = useRouter();
const comfortMode = inject<any>("comfortMode", ref("comfortable"));

// Filter out duplicates and internal-only activities
const clientActivities = computed(() => {
  if (!Array.isArray(props.data)) return [];

  const seen = new Set<string>();
  const filtered = props.data.filter((item) => {
    const type = String(item.type || "").toLowerCase();
    if (
      type === "logged_in" ||
      type === "client_created" ||
      type === "client_updated"
    ) {
      return false;
    }
    const signature = `${type}:${item.title}:${item.created_at}`;
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });

  const limit = comfortMode.value === "comfortable" ? 4 : 6;
  return filtered.slice(0, limit);
});

function formatDate(value?: string | null) {
  if (!value) return "Recently";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function iconColorClass(type: string, title = "") {
  const icon = getActivityIconName(type, title);
  if (icon === DASHBOARD_ICONS.eventCompleted) {
    return "text-emerald-600 bg-emerald-50 border-emerald-200";
  }
  if (icon === DASHBOARD_ICONS.eventCreated) {
    return "text-blue-600 bg-blue-50 border-blue-200";
  }
  if (icon === DASHBOARD_ICONS.eventUpdated) {
    return "text-indigo-600 bg-indigo-50 border-indigo-200";
  }
  if (icon === DASHBOARD_ICONS.eventReview) {
    return "text-amber-600 bg-amber-50 border-amber-200";
  }
  if (icon === DASHBOARD_ICONS.eventTicket) {
    return "text-purple-600 bg-purple-50 border-purple-200";
  }
  if (icon === DASHBOARD_ICONS.eventReport) {
    return "text-violet-600 bg-violet-50 border-violet-200";
  }
  if (icon === DASHBOARD_ICONS.eventMessage) {
    return "text-teal-600 bg-teal-50 border-teal-200";
  }
  return "text-slate-600 bg-slate-50 border-slate-200";
}

function activityActionLabel(item: any): string {
  const type = String(item.type || "").toLowerCase();
  const title = String(item.title || "").toLowerCase();

  if (type.includes("task_marked_sent") || title.includes("review")) {
    return "Review";
  }
  if (type.includes("task") || item.task_id) {
    return "View Deliverable";
  }
  if (type.includes("ticket") || item.ticket_id) {
    return "View Request";
  }
  if (type.includes("report") || item.report_id) {
    return "View Report";
  }
  return "View Details";
}

function handleItemAction(item: any) {
  emit("view-activity", item);

  if (item.task_id) {
    router.push({
      path: "/apps/task-dashboard",
      query: { taskId: String(item.task_id) },
    });
    return;
  }
  if (item.ticket_id || String(item.type).includes("ticket")) {
    router.push("/communication/chat");
    return;
  }
  if (item.report_id || String(item.type).includes("report")) {
    router.push("/reports");
    return;
  }
  router.push("/updates");
}

function handleViewAll() {
  router.push("/updates");
}

return (_ctx: any,_cache: any) => {
  const _component_ArrowRight = _resolveComponent("ArrowRight")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("div", null, [
        _createElementVNode("h3", { class: "font-black text-slate-900 text-xl md:text-2xl" }, " Recent Deliveries & Updates "),
        _createElementVNode("p", { class: "font-semibold text-sm md:text-base text-slate-600 mt-1" }, " Stay informed on recent progress and marketing deliverables. ")
      ], -1)),
      _createVNode(_unref(Button), {
        variant: "ghost",
        class: "font-bold text-[#1f7aff] hover:text-[#185adb] flex items-center gap-1 hover:bg-slate-50 h-10 text-sm md:text-base self-start sm:self-center px-3",
        onClick: handleViewAll
      }, {
        default: _withCtx(() => [
          _cache[0] || (_cache[0] = _createTextVNode(" View All Updates ", -1)),
          _createVNode(_component_ArrowRight, { class: "size-4" })
        ]),
        _: 1
      })
    ]),
    (__props.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (n) => {
            return _createVNode(_unref(Skeleton), {
              key: n,
              class: "h-28 rounded-2xl bg-slate-100 animate-pulse"
            })
          }), 64))
        ]))
      : (clientActivities.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(DMSuiteIcon, {
                icon: _unref(DASHBOARD_ICONS).calendar,
                size: 24,
                "custom-class": "text-slate-400"
              }, null, 8, ["icon"])
            ]),
            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "space-y-0.5" }, [
              _createElementVNode("p", { class: "text-base font-bold text-slate-800" }, " No recent updates yet "),
              _createElementVNode("p", { class: "text-xs text-slate-400 font-medium max-w-sm mx-auto" }, " Deliverable completions and progress milestones will appear here as work progresses. ")
            ], -1))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clientActivities.value, (item) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.id,
                class: "bg-white border border-slate-200/80 rounded-2xl shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between p-5 gap-3.5"
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["border flex items-center justify-center shrink-0 size-11 rounded-2xl", iconColorClass(item.type, item.title)])
                  }, [
                    _createVNode(DMSuiteIcon, {
                      icon: _unref(getActivityIconName)(item.type, item.title),
                      size: 22
                    }, null, 8, ["icon"])
                  ], 2),
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("h4", _hoisted_10, _toDisplayString(item.title), 1),
                      _createElementVNode("span", _hoisted_11, _toDisplayString(formatDate(item.created_at)), 1)
                    ]),
                    (item.service_name)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(item.service_name), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_13, _toDisplayString(item.description), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(_unref(Button), {
                    size: "sm",
                    variant: "ghost",
                    class: "font-bold text-[#1f7aff] hover:text-[#185adb] hover:bg-blue-50/50 h-8 px-3 text-xs rounded-xl flex items-center gap-1",
                    onClick: ($event: any) => (handleItemAction(item))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(activityActionLabel(item)) + " ", 1),
                      _createVNode(DMSuiteIcon, {
                        icon: _unref(DASHBOARD_ICONS).arrowRight,
                        size: 12
                      }, null, 8, ["icon"])
                    ]),
                    _: 2
                  }, 1032, ["onClick"])
                ])
              ]))
            }), 128))
          ]))
  ]))
}
}

})